import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Send, Loader2, Pencil, Ban, Link2, FileText, Download, ChevronDown, ChevronUp, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });
const aud = (n) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(n || 0);
const fmt = (d) => (d ? new Date(d).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "—");

const STATUS_STYLES = {
  draft: "text-[#A1A1AA] border-[#27272A]",
  sent: "text-[#DFFF00] border-[#DFFF00]/40",
  paid: "text-emerald-400 border-emerald-900",
  cancelled: "text-[#52525B] border-[#27272A]",
};

const EMPTY = { name: "", email: "", service_label: "", amount_aud: "", inclusions: "", delivery_timeframe: "", revisions: "", notes: "" };

function Field({ label, children }) {
  return (
    <div>
      <label className="tracking-label text-[10px] uppercase font-body font-bold text-[#52525B]">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full bg-[#0A0A0A] border border-[#27272A] font-body text-sm text-[#F8F8F5] p-3 focus:border-[#DFFF00] outline-none";

function DefaultsPanel({ defaults, onSaved }) {
  const [form, setForm] = useState(defaults);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(defaults), [defaults]);
  const save = async () => {
    setSaving(true);
    try {
      const r = await http.put("/admin/quote-defaults", form);
      onSaved(r.data);
      toast.success("Quote defaults saved.");
    } catch (e) {
      toast.error("Could not save defaults.");
    } finally {
      setSaving(false);
    }
  };
  const missing = !defaults.delivery_timeframe || !defaults.revisions;
  return (
    <div data-testid="quote-defaults-panel" className="border border-[#27272A] bg-[#141414] p-5 mb-6">
      <p className="font-display font-bold">Quote defaults</p>
      <p className="mt-1 font-body text-xs text-[#A1A1AA]">
        Applied to every new quote. {missing && <span className="text-[#DFFF00]">Required before quotes can be sent — please set both.</span>}
      </p>
      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <Field label="Delivery timeframe (shown to customer)">
          <input data-testid="defaults-timeframe-input" value={form.delivery_timeframe} placeholder="e.g. 7 business days from receiving your documents"
            onChange={(e) => setForm({ ...form, delivery_timeframe: e.target.value })} className={inputCls} />
        </Field>
        <Field label="Included revisions (shown to customer)">
          <input data-testid="defaults-revisions-input" value={form.revisions} placeholder="e.g. Two rounds of revisions"
            onChange={(e) => setForm({ ...form, revisions: e.target.value })} className={inputCls} />
        </Field>
      </div>
      <button data-testid="defaults-save-btn" onClick={save} disabled={saving}
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body text-sm font-semibold px-5 py-2.5 hover:bg-[#C8E600] transition-colors disabled:opacity-60">
        {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save defaults
      </button>
    </div>
  );
}

function QuoteForm({ initial, defaults, onClose, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState(
    isNew
      ? { ...EMPTY, delivery_timeframe: defaults.delivery_timeframe, revisions: defaults.revisions }
      : { ...initial, amount_aud: String(initial.amount_aud), inclusions: initial.inclusions.join("\n") }
  );
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, email: form.email, service_label: form.service_label,
        amount_aud: parseFloat(form.amount_aud || "0"),
        inclusions: form.inclusions.split("\n").map((l) => l.trim()).filter(Boolean),
        delivery_timeframe: form.delivery_timeframe, revisions: form.revisions, notes: form.notes,
      };
      const r = isNew ? await http.post("/admin/quotes", payload) : await http.put(`/admin/quotes/${initial.id}`, payload);
      toast.success(isNew ? "Quote created as a draft. Send it when you're ready." : "Quote updated.");
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the quote.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="quote-form" className="border border-[#DFFF00]/40 bg-[#141414] p-5 mb-6 space-y-4">
      <p className="font-display font-bold">{isNew ? "New quote" : `Edit quote ${initial.reference}`}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Customer name"><input data-testid="quote-field-name" value={form.name} onChange={set("name")} className={inputCls} /></Field>
        <Field label="Customer email"><input data-testid="quote-field-email" value={form.email} onChange={set("email")} className={inputCls} /></Field>
        <Field label="Service name (shown on quote and receipt)">
          <input data-testid="quote-field-service" value={form.service_label} onChange={set("service_label")} list="bcb-services" className={inputCls} />
          <datalist id="bcb-services">
            <option value="Career Strategy, CV and LinkedIn" />
            <option value="Small Business and Self Employment" />
          </datalist>
        </Field>
        <Field label="Total price (AUD)"><input data-testid="quote-field-amount" type="number" min="1" step="0.01" value={form.amount_aud} onChange={set("amount_aud")} className={inputCls} /></Field>
        <Field label="Delivery timeframe"><input data-testid="quote-field-timeframe" value={form.delivery_timeframe} onChange={set("delivery_timeframe")} className={inputCls} /></Field>
        <Field label="Included revisions"><input data-testid="quote-field-revisions" value={form.revisions} onChange={set("revisions")} className={inputCls} /></Field>
      </div>
      <Field label="Inclusions (one per line)">
        <textarea data-testid="quote-field-inclusions" rows={5} value={form.inclusions} onChange={set("inclusions")} className={inputCls} />
      </Field>
      <Field label="Notes to the customer (optional)">
        <textarea data-testid="quote-field-notes" rows={2} value={form.notes} onChange={set("notes")} className={inputCls} />
      </Field>
      <p className="font-body text-xs text-[#52525B]">Tax line shown automatically: "No GST applies. Barrett Career Brand is not registered for GST."</p>
      <div className="flex gap-3">
        <button data-testid="quote-save-btn" onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body text-sm font-semibold px-5 py-2.5 hover:bg-[#C8E600] transition-colors disabled:opacity-60">
          {saving && <Loader2 className="animate-spin" size={14} />} {isNew ? "Create draft quote" : "Save changes"}
        </button>
        <button onClick={onClose} className="rounded-full border border-[#27272A] font-body text-sm text-[#A1A1AA] px-5 py-2.5 hover:text-[#F8F8F5]">Close</button>
      </div>
    </div>
  );
}

function QuoteRow({ q, onEdit, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const r = await fn();
      onChanged(r.data);
      toast.success(okMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That action failed.");
    } finally {
      setBusy("");
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(`${window.location.origin}/quote/${q.token}`);
    toast.success("Quote link copied.");
  };

  const download = async (i, d) => {
    try {
      const r = await http.get(`/admin/quotes/${q.id}/documents/${i}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download the file.");
    }
  };

  return (
    <div data-testid={`quote-row-${q.reference}`} className="border border-[#27272A] bg-[#141414]">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-4 px-5 py-4 text-left">
        <span className={`inline-flex text-[11px] font-body font-bold uppercase tracking-wider border px-2.5 py-1 ${STATUS_STYLES[q.status]}`}>{q.status}</span>
        <span className="font-body text-sm text-[#F8F8F5] font-semibold">{q.reference}</span>
        <span className="font-body text-sm text-[#A1A1AA] truncate flex-1">{q.name} · {q.service_label}</span>
        <span className="font-body text-sm text-[#F8F8F5] tabular-nums">{aud(q.amount_aud)}</span>
        {open ? <ChevronUp size={16} className="text-[#52525B]" /> : <ChevronDown size={16} className="text-[#52525B]" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-[#27272A] pt-4 font-body text-sm text-[#A1A1AA] space-y-2">
          <p><b className="text-[#52525B]">Email:</b> {q.email}</p>
          <p><b className="text-[#52525B]">Created:</b> {fmt(q.created_at)} · <b className="text-[#52525B]">Sent:</b> {fmt(q.sent_at)} · <b className="text-[#52525B]">Paid:</b> {fmt(q.paid_at)}</p>
          <p><b className="text-[#52525B]">Timeframe:</b> {q.delivery_timeframe || "—"} · <b className="text-[#52525B]">Revisions:</b> {q.revisions || "—"}</p>
          <ul className="list-disc pl-5">{q.inclusions.map((i, k) => <li key={k}>{i}</li>)}</ul>
          {q.documents?.length > 0 && (
            <div className="pt-1">
              <p className="text-[#52525B] font-bold text-xs uppercase tracking-wider mb-1">Customer documents</p>
              {q.documents.map((d, i) => (
                <button key={i} data-testid={`quote-doc-download-${i}`} onClick={() => download(i, d)}
                  className="flex items-center gap-2 text-[#DFFF00] hover:underline underline-offset-4">
                  <FileText size={14} /> {d.original_filename} ({Math.max(1, Math.round(d.size / 1024))} KB) <Download size={13} />
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-3 pt-3">
            {q.status !== "paid" && q.status !== "cancelled" && (
              <button data-testid="quote-send-btn" disabled={!!busy}
                onClick={() => act("send", () => http.post(`/admin/quotes/${q.id}/send`), "Quote emailed to the customer.")}
                className="inline-flex items-center gap-2 rounded-full bg-[#DFFF00] text-[#0A0A0A] font-semibold px-4 py-2 text-sm hover:bg-[#C8E600] disabled:opacity-60">
                {busy === "send" ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} {q.status === "sent" ? "Resend quote" : "Send quote"}
              </button>
            )}
            {q.status !== "paid" && (
              <button data-testid="quote-edit-btn" onClick={() => onEdit(q)}
                className="inline-flex items-center gap-2 rounded-full border border-[#27272A] px-4 py-2 text-sm hover:border-[#DFFF00]">
                <Pencil size={14} /> Edit
              </button>
            )}
            {q.status !== "draft" && (
              <button data-testid="quote-copy-link-btn" onClick={copyLink}
                className="inline-flex items-center gap-2 rounded-full border border-[#27272A] px-4 py-2 text-sm hover:border-[#DFFF00]">
                <Link2 size={14} /> Copy quote link
              </button>
            )}
            {q.status !== "paid" && q.status !== "cancelled" && (
              <button data-testid="quote-cancel-btn" disabled={!!busy}
                onClick={() => act("cancel", () => http.post(`/admin/quotes/${q.id}/cancel`), "Quote cancelled.")}
                className="inline-flex items-center gap-2 rounded-full border border-[#27272A] px-4 py-2 text-sm text-red-400 hover:border-red-900 disabled:opacity-60">
                <Ban size={14} /> Cancel quote
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminQuotes() {
  const [rows, setRows] = useState([]);
  const [defaults, setDefaults] = useState({ delivery_timeframe: "", revisions: "" });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | quote

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, d] = await Promise.all([http.get("/admin/quotes"), http.get("/admin/quote-defaults")]);
      setRows(q.data);
      setDefaults(d.data);
    } catch (e) {
      toast.error("Could not load quotes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const merge = (doc) => setRows((rs) => {
    const i = rs.findIndex((r) => r.id === doc.id);
    return i === -1 ? [doc, ...rs] : rs.map((r) => (r.id === doc.id ? doc : r));
  });

  return (
    <div className="mt-8" data-testid="admin-quotes-panel">
      <DefaultsPanel defaults={defaults} onSaved={setDefaults} />
      {editing ? (
        <QuoteForm initial={editing} defaults={defaults} onClose={() => setEditing(null)} onSaved={(d) => { merge(d); setEditing(null); }} />
      ) : (
        <button data-testid="quote-new-btn" onClick={() => setEditing({})}
          className="mb-6 inline-flex items-center gap-2 rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body text-sm font-semibold px-5 py-2.5 hover:bg-[#C8E600] transition-colors">
          <Plus size={15} /> New quote
        </button>
      )}
      {loading ? (
        <p className="font-body text-sm text-[#52525B]">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="font-body text-[#A1A1AA]">No quotes yet. Create one from an enquiry in the Consultations tab.</p>
      ) : (
        <div className="space-y-3" data-testid="quotes-rows">
          {rows.map((q) => <QuoteRow key={q.id} q={q} onEdit={setEditing} onChanged={merge} />)}
        </div>
      )}
    </div>
  );
}
